import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Radio, ExternalLink, Loader2, Pencil, Check, X, AlertCircle, Sparkles } from 'lucide-react';
import { liveBroadcastsApi, metadataTemplatesApi, queryKeys, type LiveBroadcast, type MetadataTemplate } from '@/lib/api';
import { useAuth } from '@/store/auth';
import MetadataTemplatesManager from '@/components/MetadataTemplatesManager';

const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

function BroadcastRow({ facilityId, b, templates }: { facilityId: string; b: LiveBroadcast; templates: MetadataTemplate[] }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(b.title ?? '');
  const [description, setDescription] = useState(b.description ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<number | ''>('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.liveBroadcasts.list(facilityId) });

  const save = useMutation({
    mutationFn: () => liveBroadcastsApi.update(facilityId, { sheetName: b.sheetName, title: title.trim(), description }),
    onSuccess: () => {
      setErr(null);
      setEditing(false);
      invalidate();
    },
    onError: (e: any) => setErr(e?.message || 'Update failed'),
  });

  const apply = useMutation({
    mutationFn: (id: number) => liveBroadcastsApi.applyTemplate(facilityId, { sheetName: b.sheetName, templateId: id }),
    onSuccess: (r) => {
      setErr(null);
      setMsg(`Applied "${r.title}"${r.thumbnailApplied ? ' + thumbnail' : (r.thumbnailError ? ' (thumbnail failed: ' + r.thumbnailError + ')' : '')}.`);
      setTimeout(() => setMsg(null), 7000);
      invalidate();
    },
    onError: (e: any) => setErr(e?.message || 'Apply failed'),
  });

  const startEdit = () => {
    setTitle(b.title ?? '');
    setDescription(b.description ?? '');
    setErr(null);
    setEditing(true);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-600 dark:bg-red-900/40 dark:text-red-400">
              Sheet {b.sheetName}
            </span>
            {b.isUnscheduled && (
              <span
                title="Started manually from the broadcast host — not from the schedule"
                className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              >
                Ad-hoc
              </span>
            )}
            {b.youtubeStatus && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                {b.youtubeStatus}
              </span>
            )}
            <a href={b.watchUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400">
              <ExternalLink className="h-3 w-3" /> Watch
            </a>
          </div>

          {!editing ? (
            <>
              <p className="mt-2 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                {b.found ? (b.title || <span className="text-gray-400">(no title)</span>) : <span className="text-amber-600 dark:text-amber-400">Broadcast not found on YouTube</span>}
              </p>
              {b.found && b.description && (
                <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-gray-500 dark:text-gray-400">{b.description}</p>
              )}
              {b.found && templates.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-gray-400" />
                  <select
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <option value="">Apply template…</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.isDefault ? ' (default)' : ''}</option>)}
                  </select>
                  <button
                    disabled={templateId === '' || apply.isPending}
                    onClick={() => { if (templateId !== '') { if (window.confirm('Apply this template to the live stream? It overwrites the current title/description' + '.')) apply.mutate(templateId); } }}
                    className="flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    {apply.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
                  </button>
                </div>
              )}
              {msg && <p className="mt-1 text-xs text-green-600 dark:text-green-400">{msg}</p>}
              {err && !editing && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{err}</p>}
            </>
          ) : (
            <div className="mt-2 space-y-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Title <span className="text-gray-400">({title.length}/100)</span></label>
                <input className={inputCls} maxLength={100} value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Description</label>
                <textarea className={inputCls} rows={4} maxLength={5000} value={description} onChange={(e) => setDescription(e.target.value)} />
              </div>
              {err && (
                <div className="flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {err}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditing(false)} className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">
                  <X className="h-3.5 w-3.5" /> Cancel
                </button>
                <button onClick={() => save.mutate()} disabled={save.isPending || title.trim().length === 0}
                  className="flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                  {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
                </button>
              </div>
            </div>
          )}
        </div>

        {!editing && b.found && (
          <button onClick={startEdit}
            className="flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>
    </div>
  );
}

export default function LiveStreamsTab({ facilityId }: { facilityId: string | null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.liveBroadcasts.list(facilityId ?? ''),
    queryFn: () => liveBroadcastsApi.list(facilityId as string),
    enabled: !!facilityId,
    refetchInterval: 30_000,
  });

  const { data: templatesData } = useQuery({
    queryKey: queryKeys.metadataTemplates.list(facilityId ?? ''),
    queryFn: () => metadataTemplatesApi.list(facilityId as string),
    enabled: !!facilityId,
  });
  const templates = templatesData?.templates ?? [];

  if (!facilityId) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Select a facility to see its live streams.</p>;
  }
  if (isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  }
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
        {(error as any)?.message || 'Failed to load live streams'}
      </div>
    );
  }

  const broadcasts = data?.broadcasts ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Radio className="h-4 w-4 text-red-500" />
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Live streams</h2>
        <span className="text-xs text-gray-400">{broadcasts.length} active — edit the YouTube title & description on air</span>
      </div>

      {broadcasts.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-200 py-10 text-center text-sm text-gray-400 dark:border-gray-700">
          No live broadcasts right now. Titles &amp; descriptions can be edited here once a sheet goes live.
        </p>
      ) : (
        <div className="space-y-2">
          {broadcasts.map((b) => (
            <BroadcastRow key={b.broadcastId} facilityId={facilityId} b={b} templates={templates} />
          ))}
        </div>
      )}

      {/* Template management — admins only */}
      {isAdmin && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/50 p-4 dark:border-gray-700 dark:bg-gray-800/30">
          <MetadataTemplatesManager facilityId={facilityId} />
        </div>
      )}
    </div>
  );
}
